import PropTypes from 'prop-types';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { batch } from 'react-redux';

// material-ui
import { TextField, Box } from '@mui/material';

// third party
import { useIntl } from 'react-intl';

// project imports
import { useSelector, useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { ApiUsersGetCollection } from 'store/app/admin/user';
import Autocomplete from 'ui-component/extended/Autocomplete';

// ==============================|| SELECT USER FIELD ||============================== //

export const renderUser = (user, displayMode = 'username') => {
    if (!user || Array.isArray(user)) return '';
    if (typeof user === 'string') return user;

    if (displayMode === 'fullName') {
        const fullName = [user?.name, user?.lastName].filter(Boolean).join(' ').trim();
        return fullName || user?.username || '';
    }
    return user.username || '';
};

const UserSelect = ({
    value,
    onChange = () => {},
    validationErrors = null,
    baseId = '',
    displayMode = 'username',
    enableSearch = true,
    loadData = false,
    baseFilterGroups = [],
    ...others
}) => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const { list: userList, quickSearchFilterDef: userQuickSearchFilterDef } = useSelector((state) => state.user);

    const [isLoadingUser, setIsLoadingUser] = useState(false);
    const [userInputUser, setUserInputUser] = useState('');
    const [users, setUsers] = useState(userList || []);

    const userFilterRows = useMemo(
        () =>
            userQuickSearchFilterDef?.fields?.map((fd) => ({
                field: fd,
                operator: 'like',
                value: userInputUser
            })),
        [userQuickSearchFilterDef, userInputUser]
    );

    const handleLoadUsers = useCallback(() => {
        setIsLoadingUser(true);
        ApiUsersGetCollection({
            filters: baseFilterGroups,
            successCallback: (response) =>
                batch(() => {
                    setIsLoadingUser(false);
                    setUsers(response);
                    setUserInputUser('');
                }),
            errorCallback: (error) =>
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                ),
            updateStore: false
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseFilterGroups]);

    useEffect(() => {
        if (loadData) {
            handleLoadUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    useEffect(() => {
        if (enableSearch && userInputUser?.length >= 3) {
            setIsLoadingUser(true);
            ApiUsersGetCollection({
                filters: [...baseFilterGroups, { filterType: 'or', filterRows: userFilterRows }],
                successCallback: (response) =>
                    batch(() => {
                        setIsLoadingUser(false);
                        setUsers(response);
                    }),
                errorCallback: (error) =>
                    batch(() => {
                        setIsLoadingUser(false);
                        dispatch(
                            openSnackbar({
                                open: true,
                                message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                                variant: 'alert',
                                alert: { color: 'error' }
                            })
                        );
                    }),
                updateStore: false
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableSearch, userFilterRows]);

    return (
        <Autocomplete
            id={`${baseId}-user`}
            value={value}
            onChange={(value) =>
                batch(() => {
                    onChange(value);
                    setUserInputUser(renderUser(value, displayMode));
                })
            }
            inputValue={userInputUser || renderUser(value, displayMode)}
            onInputChange={(value, reason) => {
                if (reason === 'input') {
                    setUserInputUser(value);
                }
                if (reason === 'clear') {
                    handleLoadUsers();
                }
            }}
            loading={isLoadingUser}
            options={users}
            freeSolo={enableSearch}
            getOptionLabel={(option) => renderUser(option, displayMode)}
            renderOption={(props, option) => (
                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                    {option?.avatar && (
                        <img loading="lazy" width="20" src={`${process.env.REACT_APP_API}/${option?.avatar || ''}`} alt="" />
                    )}
                    {renderUser(option, displayMode)}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputProps={{ ...params.inputProps }}
                    error={Boolean(validationErrors)}
                    helperText={validationErrors}
                />
            )}
            {...others}
        />
    );
};

UserSelect.propTypes = {
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
    onChange: PropTypes.func,
    validationErrors: PropTypes.any,
    baseId: PropTypes.string,
    displayMode: PropTypes.oneOf(['username', 'fullName']),
    enableSearch: PropTypes.bool,
    loadData: PropTypes.bool,
    baseFilterGroups: PropTypes.array
};

export default UserSelect;
